// src/components/lesson/diagrams/NumberLine.tsx
//
// A number line with optional markers and intervals. Useful for
// inequalities, intervals, and showing where roots fall.
//
// Params:
//   {
//     "min": -5, "max": 10,
//     "marks": [-3, 0, 5],          // labeled tick points
//     "interval": { "from": 2, "to": 7, "open": false } // optional shaded segment
//   }

type NumberLineParams = {
  min?: number
  max?: number
  marks?: number[]
  interval?: { from: number; to: number; open?: boolean }
  label?: string
}

const W = 360
const H = 70
const PAD = 24

export default function NumberLine({ params }: { params: Record<string, unknown> }) {
  const p = params as NumberLineParams
  const min = typeof p.min === 'number' ? p.min : -5
  const max = typeof p.max === 'number' ? p.max : 5
  const marks = Array.isArray(p.marks)
    ? p.marks.filter((n): n is number => typeof n === 'number')
    : []
  const interval = p.interval

  const toPx = (x: number) => PAD + ((x - min) / (max - min)) * (W - 2 * PAD)
  const yMid = H / 2

  // Integer ticks
  const ticks: number[] = []
  for (let x = Math.ceil(min); x <= Math.floor(max); x++) ticks.push(x)

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Number line'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Shaded interval (rendered behind ticks) */}
      {interval && (
        <rect
          x={toPx(interval.from)}
          y={yMid - 6}
          width={Math.max(2, toPx(interval.to) - toPx(interval.from))}
          height={12}
          fill="rgb(34 211 238 / 0.25)"
        />
      )}

      {/* Main axis */}
      <line x1={PAD} x2={W - PAD} y1={yMid} y2={yMid} stroke="rgb(148 163 184)" strokeWidth="1.5" />

      {/* Integer ticks */}
      {ticks.map((t) => (
        <g key={t}>
          <line
            x1={toPx(t)}
            x2={toPx(t)}
            y1={yMid - 4}
            y2={yMid + 4}
            stroke="rgb(100 116 139)"
            strokeWidth="1"
          />
          <text x={toPx(t)} y={yMid + 18} fontSize="10" textAnchor="middle" fill="rgb(148 163 184)">
            {t}
          </text>
        </g>
      ))}

      {/* Interval endpoints — closed (filled) or open (unfilled) circles */}
      {interval && (
        <>
          <circle
            cx={toPx(interval.from)}
            cy={yMid}
            r="4.5"
            fill={interval.open ? 'rgb(15 23 42)' : 'rgb(34 211 238)'}
            stroke="rgb(34 211 238)"
            strokeWidth="2"
          />
          <circle
            cx={toPx(interval.to)}
            cy={yMid}
            r="4.5"
            fill={interval.open ? 'rgb(15 23 42)' : 'rgb(34 211 238)'}
            stroke="rgb(34 211 238)"
            strokeWidth="2"
          />
        </>
      )}

      {/* Labeled marks (on top of everything) */}
      {marks.map((m) => (
        <g key={`mark-${m}`}>
          <circle
            cx={toPx(m)}
            cy={yMid}
            r="4"
            fill="rgb(251 191 36)"
            stroke="rgb(15 23 42)"
            strokeWidth="1.5"
          />
          <text
            x={toPx(m)}
            y={yMid - 10}
            fontSize="11"
            fontWeight="600"
            textAnchor="middle"
            fill="rgb(251 191 36)"
          >
            {m}
          </text>
        </g>
      ))}
    </svg>
  )
}
